// Dedicated USB printer module for barcode LABEL printing.
// Kept separate from usbPrinter.ts (used for receipts) so changes here
// never risk breaking receipt printing, and vice versa.
//
// Exports the same function names as usbPrinter.ts so ProductCatalogView.tsx
// only needs to change its import path:
//   import { printElementUSB, isUSBPrinterConnected, connectUSBPrinter,
//            tryAutoReconnectUSB, getUSBPrinterName } from '../utils/labelUsbPrinter';

import html2canvas from 'html2canvas';

const USB_PRINTER_NAME_KEY = 'usb_label_printer_name'; // separate key from the receipt printer
const DEBUG = false; // flip on locally if you need the console logs / preview window back

function removeToast() {
  document.getElementById('bt-toast')?.remove();
}

function showToast(msg: string, color = '#1e293b', autoDismiss = 0) {
  removeToast();
  const t = document.createElement('div');
  t.id = 'bt-toast';
  t.style.cssText = `position:fixed;top:24px;left:50%;transform:translateX(-50%);background:${color};color:#fff;padding:14px 22px;border-radius:14px;font-family:system-ui,sans-serif;z-index:100000;box-shadow:0 8px 32px rgba(0,0,0,0.35);font-size:13px;font-weight:600;min-width:280px;text-align:center;`;
  t.textContent = msg;
  document.body.appendChild(t);
  if (autoDismiss) setTimeout(removeToast, autoDismiss);
}

let usbDevice: any = null;
let usbEndpointOut: number | null = null;

async function claimUsbDevice(device: any): Promise<boolean> {
  try {
    await device.open();
    if (device.configuration === null) await device.selectConfiguration(1);
    const iface = device.configuration.interfaces.find((i: any) =>
      i.alternates.some((a: any) => a.endpoints.some((e: any) => e.direction === 'out'))
    );
    if (!iface) throw new Error('No writable USB endpoint found on this device');
    await device.claimInterface(iface.interfaceNumber);
    const alt = iface.alternates.find((a: any) => a.endpoints.some((e: any) => e.direction === 'out'));
    const outEp = alt.endpoints.find((e: any) => e.direction === 'out');
    usbDevice = device;
    usbEndpointOut = outEp.endpointNumber;
    return true;
  } catch (e) {
    console.error('USB claim failed:', e);
    return false;
  }
}

export async function connectUSBPrinter(): Promise<boolean> {
  const usb = (navigator as any).usb;
  if (!usb) {
    alert('WebUSB is not supported in this browser. Use Chrome or Edge (desktop or Android).');
    return false;
  }

  showToast('🔍 Opening USB device picker...', '#1e293b');
  try {
    const device = await usb.requestDevice({ filters: [] });
    removeToast();
    showToast(`🔗 Connecting to ${device.productName || 'USB printer'}...`, '#1e293b');

    const ok = await claimUsbDevice(device);
    removeToast();
    if (!ok) {
      alert('Connected but could not claim a writable USB interface on this device.');
      return false;
    }
    localStorage.setItem(USB_PRINTER_NAME_KEY, device.productName || 'USB Printer');
    return true;
  } catch (e: any) {
    removeToast();
    if (e?.name !== 'NotFoundError') console.error('USB connect failed:', e);
    return false;
  }
}

// Silent reconnect to a previously-granted USB device — no picker needed.
export async function tryAutoReconnectUSB(): Promise<boolean> {
  const usb = (navigator as any).usb;
  if (!usb) return false;
  try {
    const devices = await usb.getDevices();
    if (devices.length === 0) return false;
    return await claimUsbDevice(devices[0]);
  } catch {
    return false;
  }
}

export function isUSBPrinterConnected(): boolean {
  return !!usbDevice;
}

export function disconnectUSBPrinter() {
  usbDevice?.close?.().catch(() => {});
  usbDevice = null;
  usbEndpointOut = null;
  localStorage.removeItem(USB_PRINTER_NAME_KEY);
}

export function getUSBPrinterName(): string | null {
  return localStorage.getItem(USB_PRINTER_NAME_KEY);
}

// Sends raw bytes to the printer in large USB-bulk-transfer-safe chunks,
// with NO artificial delay between them. 16KB comfortably fits a single
// bulk transfer on virtually all USB thermal/label printers, and letting
// transferOut's own promise resolution pace the writes (instead of a fixed
// setTimeout) avoids starving the printer's buffer — which is what was
// causing the stuttering "tak tak tak" print-head noise before.
async function sendUSB(bytes: Uint8Array) {
  if (!usbDevice || usbEndpointOut === null) throw new Error('No USB printer connected');
  const CHUNK = 16384;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    await usbDevice.transferOut(usbEndpointOut, bytes.slice(i, i + CHUNK));
  }
}

const DEFAULT_PRINTER_DOTS_WIDTH = 576; // 576 = 80mm, 384 = 58mm (at 203dpi)

async function elementToRasterBytes(
  elementId: string,
  printerWidthDots = DEFAULT_PRINTER_DOTS_WIDTH
): Promise<number[]> {
  const el = document.getElementById(elementId);
  if (!el) throw new Error(`Element #${elementId} not found`);

  const canvas = await html2canvas(el, {
    backgroundColor: '#ffffff',
    scale: 1,
    useCORS: true,
    foreignObjectRendering: true,
  });

  if (DEBUG) console.log('[LABEL PRINT] captured canvas size:', canvas.width, canvas.height);

  const scale = printerWidthDots / canvas.width;
  const targetH = Math.max(1, Math.round(canvas.height * scale));

  const off = document.createElement('canvas');
  off.width = printerWidthDots;
  off.height = targetH;
  const ctx = off.getContext('2d')!;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, off.width, off.height);
  ctx.drawImage(canvas, 0, 0, off.width, off.height);

  const { data } = ctx.getImageData(0, 0, off.width, off.height);
  const widthBytes = Math.ceil(printerWidthDots / 8);
  const pixels = new Uint8Array(widthBytes * targetH);

  for (let y = 0; y < targetH; y++) {
    for (let x = 0; x < printerWidthDots; x++) {
      const i = (y * printerWidthDots + x) * 4;
      const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
      const lum = r * 0.299 + g * 0.587 + b * 0.114;
      const isBlack = a > 10 && lum < 200;
      if (isBlack) {
        const byteIndex = y * widthBytes + Math.floor(x / 8);
        const bit = 7 - (x % 8);
        pixels[byteIndex] |= 1 << bit;
      }
    }
  }

  const xL = widthBytes & 0xff, xH = (widthBytes >> 8) & 0xff;
  const yL = targetH & 0xff, yH = (targetH >> 8) & 0xff;

  if (DEBUG) console.log(`[LABEL PRINT] raster built: ${printerWidthDots}x${targetH} dots, ${pixels.length} image bytes`);
  return [0x1D, 0x76, 0x30, 0x00, xL, xH, yL, yH, ...Array.from(pixels)];
}

export async function printElementUSB(
  elementId: string,
  printerWidthDots = DEFAULT_PRINTER_DOTS_WIDTH
): Promise<boolean> {
  try {
    if (!isUSBPrinterConnected()) {
      const connected = await connectUSBPrinter();
      if (!connected) return false;
    }

    showToast('📸 Rendering label...', '#1e293b');
    const raster = await elementToRasterBytes(elementId, printerWidthDots);

    showToast('📄 Printing label...', '#1d4ed8');
    const FEED = [0x1B, 0x64, 0x04];
    const CUT = [0x1D, 0x56, 0x41, 0x10];
    const fullPayload = new Uint8Array([...raster, ...FEED, ...CUT]);

    if (DEBUG) console.log(`[LABEL PRINT] payload=${fullPayload.length} bytes`);
    await sendUSB(fullPayload);
    if (DEBUG) console.log('[LABEL PRINT] done');

    showToast('✅ Printed successfully!', '#15803d', 2500);
    return true;
  } catch (e) {
    removeToast();
    console.error('USB label print failed:', e);
    alert(`Print failed: ${(e as Error).message}`);
    return false;
  }
}